import { ReactElement, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { RHFValidations, TOAST_MESSAGES } from '../../constants/global-constants';
import authenticateService from '../../services/authenticateService';
import CustomInputBox from '../../controls/CustomInputBox';
import parsePasswordValidationMap from '../../_helpers/passwordValidation';
import PasswordInputBox from '../../controls/PasswordInputBox';
import { AwsLoginErrors } from '../../constants/telosair-interfaces';

type FORMDATA = {
  password: string;
  confirmPassword: string;
  otp: string;
  email: string;
};

/**
 * ForgotPassword.
 * @returns - React component for ForgotPassword UI.
 */
export default function ResetPasswordComponent(): ReactElement {
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const username = params.get('username') || '';
  const navigate = useNavigate();

  const methods = useForm<FORMDATA>({
    mode: 'all',
    defaultValues: useMemo(() => {
      return {
        email: username
      };
    }, [username])
  });

  const { handleSubmit, watch, reset } = methods;

  useEffect(() => {
    reset({
      email: username
    });
  }, [username]);

  /**
   * HandlesReset password.
   * @param  data - The form submit event.
   */
  const handleResetPassword = async (data: FORMDATA): Promise<void> => {
    if (username) {
      const { password, otp } = data;
      setLoading(true);
      try {
        const decodedUsername = decodeURIComponent(username);
        const { error } = await authenticateService.resetPassword(decodedUsername, otp, password);
        if (!error) {
          toast.success('Password reset successful');
          navigate(`/auth/login`);
          setLoading(false);
          return;
        }
        if (error === AwsLoginErrors.expiredCode) {
          toast.error('Invalid verification code');
          setLoading(false);
          return;
        }
        toast.error(TOAST_MESSAGES.error);
        setLoading(false);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
        toast.error(TOAST_MESSAGES.error);
      }
    } else {
      navigate(`/auth/login`);
    }
  };

  const confirmPasswordValidation = parsePasswordValidationMap(
    'confirm password',
    watch('password')
  );

  return (
    <div className="flex flex-col gap-8 w-full">
      <div className="text-3xl font-bold dark:text-white text-black text-center">
        Welcome to TelosAir
      </div>
      {/* <div className="text-gray-400 mt-1">Start managing your devices for free</div> */}
      <div className="dark:bg-container-700 bg-white p-6 rounded w-full">
        <div className="flex gap-3 items-center">
          <button
            onClick={() => navigate(-1)}
            type="button"
            className="font-medium text-gray-300 text-xl material-icons-outlined"
          >
            arrow_back
          </button>
          <div className="text-xl dark:text-white text-black font-semibold">Reset Password</div>
        </div>
        <div className="text-gray-400 mt-1">Enter the verification code and new password.</div>
        {/* eslint-disable-next-line */}
        <FormProvider {...methods}>
          <form
            className="mt-8 2xl:mt-12"
            action="post"
            onSubmit={handleSubmit(handleResetPassword)}
          >
            <CustomInputBox
              label="Email"
              idname="email"
              type="email"
              autoComplete="off"
              isHidden
              validationObject={RHFValidations.emailRequired}
            />
            <CustomInputBox
              label="Verification Code"
              idname="otp"
              type="number"
              autoComplete="off"
              validationObject={RHFValidations.otp}
            />
            <PasswordInputBox
              label="Password"
              idname="password"
              autoComplete="new-password"
              validationObject={RHFValidations.password}
            />
            <PasswordInputBox
              label="Confirm Password"
              idname="confirmPassword"
              autoComplete="off"
              validationObject={confirmPasswordValidation}
            />
            <button
              type="submit"
              className="w-full relative mt-12 text-white bg-primary-600 hover:bg-primary-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg flex justify-center items-center text-sm px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
            >
              {loading ? <ClipLoader size={20} color="#fff" /> : 'Reset Password'}
            </button>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
